import { normalizeTradeType, type TradeInvoiceType } from '../lib/trade-invoice-type';
import { resolveNoteKind } from '../lib/invoice-label';
import { AdjustmentNoteDialog } from './adjustment-note-dialog';
import {
  DownpaymentInvoiceDialog,
  type TicketContextForDownpayment,
} from './downpayment-invoice-dialog';
import { FinalSettlementInvoiceDialog } from './final-settlement-invoice-dialog';
import { ProvisionalInvoiceDialog } from './provisional-invoice-dialog';

import type { AdvancePrefill } from './downpayment-invoice-payload';
import type { AdjustmentNoteSubmitValues } from './adjustment-note-schema';
import type {
  BlContextForTradeInvoice,
  TicketContextForTradeInvoice,
  TradePrefill,
} from './trade-invoice-payload';
import type { InvoiceRow } from '../types';

// ── The invoice editor ──────────────────────────────────────────────────────
//
// One entry point for creating and editing every invoice kind. Callers name
// the kind (or let `editorSpecFor` classify an existing row) and the editor
// hosts the kind's engine — schema, defaults, gates, save mutation — inside
// the shared two-pane document surface: model-driven fields on the left, the
// PDF attach/preview pane on the right, the review-owned document values
// shown with their resolved system values. Which fields appear per kind and
// side is the field model's decision, not a per-dialog list.

export type InvoiceEditorSpec =
  | { readonly kind: 'trade'; readonly type: TradeInvoiceType; readonly prefill?: TradePrefill }
  | { readonly kind: 'advance'; readonly prefill?: AdvancePrefill }
  | {
      readonly kind: 'adjustment';
      readonly noteType: 'Credit' | 'Debit';
      readonly prefillParentId?: string;
      readonly prefillValues?: Partial<AdjustmentNoteSubmitValues>;
    };

// The editor spec for an existing row, or null when the row's kind is not
// editable through the editor.
export function editorSpecFor(invoice: InvoiceRow): InvoiceEditorSpec | null {
  const trade = normalizeTradeType(invoice.invoice_type);
  if (trade !== null) return { kind: 'trade', type: trade };
  if (invoice.invoice_type === 'Advance') return { kind: 'advance' };
  const noteKind = resolveNoteKind(invoice);
  if (noteKind !== null) return { kind: 'adjustment', noteType: noteKind };
  return null;
}

export interface InvoiceEditorDialogProps {
  readonly spec: InvoiceEditorSpec;
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly side: 'buy' | 'sell';
  readonly orderId: string;
  readonly blOrderId: string;
  readonly existingInvoice: InvoiceRow | null;
  readonly ticket: TicketContextForTradeInvoice;
  readonly bl: BlContextForTradeInvoice;
  readonly onSaved: () => void;
}

// The advance engine reads every field from the existing invoice or prefill;
// the ticket's downpayment-schedule fields only seed the order-level create
// path, so the editor carries the counterparty/currency from the trade
// context.
function toDownpaymentTicket(ticket: TicketContextForTradeInvoice): TicketContextForDownpayment {
  return {
    currency: ticket.currency,
    counterpartyName: ticket.counterpartyName,
    downpaymentAmount: null,
    downpaymentDueDate: null,
  };
}

export function InvoiceEditorDialog(props: InvoiceEditorDialogProps) {
  const { spec, ...shared } = props;
  if (spec.kind === 'trade') {
    const prefillProp = spec.prefill !== undefined ? { prefill: spec.prefill } : {};
    if (spec.type === 'Provisional') {
      return <ProvisionalInvoiceDialog {...shared} {...prefillProp} />;
    }
    return <FinalSettlementInvoiceDialog {...shared} {...prefillProp} />;
  }
  if (spec.kind === 'advance') {
    const prefillProp = spec.prefill !== undefined ? { prefill: spec.prefill } : {};
    return (
      <DownpaymentInvoiceDialog
        open={shared.open}
        onOpenChange={shared.onOpenChange}
        side={shared.side}
        orderId={shared.orderId}
        existingInvoice={shared.existingInvoice}
        ticket={toDownpaymentTicket(shared.ticket)}
        orderBls={[]}
        onSaved={shared.onSaved}
        {...prefillProp}
      />
    );
  }
  return (
    <AdjustmentNoteDialog
      open={shared.open}
      onOpenChange={shared.onOpenChange}
      side={shared.side}
      noteType={spec.noteType}
      orderId={shared.orderId}
      blOrderId={shared.blOrderId}
      blOrderName={shared.bl.blOrderName}
      existingInvoice={shared.existingInvoice}
      onSaved={shared.onSaved}
      {...(spec.prefillParentId !== undefined ? { prefillParentId: spec.prefillParentId } : {})}
      {...(spec.prefillValues !== undefined ? { prefillValues: spec.prefillValues } : {})}
    />
  );
}
